import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDesktopDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const topCategories = [
    { label: 'Popular', items: ['Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets'] },
    { label: 'Apparel', items: ['Pants/Shorts', 'Headwear', 'Sets', 'Underwear/Underpants', 'Jersey'] },
    { label: 'Accessories', items: ['Accessories', 'Bags', 'Belts', 'Perfumes', 'Other Stuff'] },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap cursor-pointer hover:text-primary-600 transition-colors duration-200"
          >
            KakobuyFinds
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${desktopDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {desktopDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/kakobuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Guides
            </Link>

            <Link
              to="/kakobuy-safety-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Safety
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-foreground-900 bg-background-200/70 rounded-full hover:bg-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-background-50 border-t border-background-200/70 py-4 px-2 space-y-1 max-h-[80vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-home-line text-base"></i>
              </span>
              Home
            </Link>

            <div className="mt-2 px-4 py-2">
              <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">Categories</p>
              <div className="space-y-3">
                {topCategories.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold text-foreground-500 mb-1.5">{group.label}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {categories
                        .filter((cat) => group.items.includes(cat.name))
                        .map((cat) => (
                          <a
                            key={cat.name}
                            href={cat.href}
                            target="_blank"
                            rel="noopener nofollow"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
                          >
                            <span className="w-5 h-5 flex items-center justify-center">
                              <i className={`${cat.icon} text-base`}></i>
                            </span>
                            <span className="truncate">{cat.name}</span>
                          </a>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 mt-2 pt-2 space-y-1">
              <Link
                to="/kakobuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
              >
                <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                  <i className="ri-book-open-line text-base"></i>
                </span>
                Guides
              </Link>
              <Link
                to="/kakobuy-safety-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
              >
                <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                  <i className="ri-shield-check-line text-base"></i>
                </span>
                Safety
              </Link>
            </div>

            <div className="pt-3 flex flex-col gap-2 px-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-2.5 text-sm font-semibold text-center text-foreground-900 bg-background-200/70 rounded-full hover:bg-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Kakobuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-2.5 text-sm font-semibold text-center text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}